import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Spinner, Button, Badge, Alert, Image, Tabs, Tab } from "react-bootstrap";
import ReviewForm from "./ReviewForm";
import ReviewList from "./ReviewList";
import EditReviewModal from "./EditReviewModal";
import NearbyRestaurants from "./NearbyRestaurants";
import SimilarRestaurants from "./SimilarRestaurants";
import ContributePhotoModal from "./ContributePhotoModal";
import CroppedImage from "./CroppedImage";
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { generateSlug } from '../utils/slugUtils';
import { renderRichText } from '../utils/richText';
import { isGuestSaved, toggleGuestSave } from '../hooks/useGuestSaves';
import { getLocationStatusBadge, getLocationCardStyle, formatExpectedDate, formatClosedDate } from '../utils/statusUtils';
import { DateTime } from 'luxon';
import { ownershipStatus } from '../utils/ownership';
import { openStatus } from '../utils/openStatus';
import { menuItemPrice, hasPriceVariants, orderedPrices, variantName, amount, unitSuffix } from '../utils/priceDisplay';

function RestaurantDetail() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth(); // token: view pending restaurants; user: detect own review (#38)
  const [showContribute, setShowContribute] = useState(false); // #99 "Add a photo" community contribution
  const { setPageTitle } = usePageTitle();
  const [restaurant, setRestaurant] = useState(null);
  const [flickrImages, setFlickrImages] = useState([]);
  const [restaurantImages, setRestaurantImages] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [menuLoading, setMenuLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshReviews, setRefreshReviews] = useState(0);
  const [aggregation, setAggregation] = useState(null); // #130: overall restaurant rating headline
  const [myReview, setMyReview] = useState(null); // current user's existing review for this restaurant (#38)
  const [showEditReview, setShowEditReview] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Helper function to make authenticated fetch calls
  const authenticatedFetch = async (url, options = {}) => {
    const headers = {
      ...options.headers,
    };

    // Add Authorization header if user is logged in
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, { ...options, headers });
  };

  // Helper function to group consecutive days with same hours
  const groupHours = (location) => {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayFields = ['monday_hours', 'tuesday_hours', 'wednesday_hours',
                      'thursday_hours', 'friday_hours', 'saturday_hours', 'sunday_hours'];

    const hoursData = dayFields.map((field, idx) => ({
      day: dayNames[idx],
      dayAbbr: dayNames[idx].substring(0, 3),
      hours: location[field] || null,
      index: idx
    })).filter(d => d.hours); // Only include days with hours

    if (hoursData.length === 0) return [];

    // Group consecutive days with same hours
    const groups = [];
    let currentGroup = [hoursData[0]];

    for (let i = 1; i < hoursData.length; i++) {
      const prev = hoursData[i - 1];
      const curr = hoursData[i];

      // Check if consecutive and same hours
      if (curr.index === prev.index + 1 && curr.hours === prev.hours) {
        currentGroup.push(curr);
      } else {
        groups.push({
          days: currentGroup,
          hours: currentGroup[0].hours,
          startIndex: currentGroup[0].index,
          endIndex: currentGroup[currentGroup.length - 1].index
        });
        currentGroup = [curr];
      }
    }

    // Push last group
    if (currentGroup.length > 0) {
      groups.push({
        days: currentGroup,
        hours: currentGroup[0].hours,
        startIndex: currentGroup[0].index,
        endIndex: currentGroup[currentGroup.length - 1].index
      });
    }

    return groups;
  };

  // Helper to format day range
  const formatDayRange = (group) => {
    if (group.days.length === 1) {
      return group.days[0].dayAbbr;
    } else if (group.days.length === 2) {
      return `${group.days[0].dayAbbr}, ${group.days[1].dayAbbr}`;
    } else {
      return `${group.days[0].dayAbbr}-${group.days[group.days.length - 1].dayAbbr}`;
    }
  };

  const fetchMenuItems = async (restaurantId) => {
    setMenuLoading(true);
    try {
      // #260: owner-aware endpoint — the menu of your OWN private restaurant
      // comes back only with the token, exactly as #248's sub-routes do.
      const response = await authenticatedFetch(`${apiBaseUrl}/foods/menu/restaurant/${restaurantId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Received menu items:", data);
      setMenuItems(data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
      // Don't set error state here as it's not critical - just log it
      setMenuItems([]);
    } finally {
      setMenuLoading(false);
    }
  };

  const fetchRestaurantImages = async (restaurantId) => {
    setImagesLoading(true);
    try {
      // Fetch restaurant ambiance/exterior images only (not menu item images).
      // #248: send the token so an owner still sees photos on their OWN private
      // restaurant — the endpoint is owner-aware now and 404s anonymous callers.
      const response = await fetch(`${apiBaseUrl}/restaurants/${restaurantId}/images`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Received restaurant images:", data);
      setRestaurantImages(data.images || []);
    } catch (error) {
      console.error("Error fetching restaurant images:", error);
      // Don't show error - images are optional
      setRestaurantImages([]);
    } finally {
      setImagesLoading(false);
    }
  };

  const extractFlickrImages = async (flickrUrl, restaurantName) => {
    setImagesLoading(true);
    try {
      // Use the backend API to extract real images from Flickr (#248: owner-aware)
      const response = await fetch(`${apiBaseUrl}/restaurants/${restaurant.id}/flickr-images`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Flickr images response:", data);

      if (data.success && data.image_urls && data.image_urls.length > 0) {
        setFlickrImages(data.image_urls);
      } else {
        // If no images found, just set empty array
        setFlickrImages([]);
      }
    } catch (error) {
      console.error("Error loading Flickr images:", error);
      // Show empty on error
      setFlickrImages([]);
    } finally {
      setImagesLoading(false);
    }
  };

  // Build a small display snapshot for guest (localStorage) saves — issue #13.
  const buildSaveSnapshot = () => {
    const mainLoc =
      restaurant.locations?.find((l) => l.is_main_location) || restaurant.locations?.[0];
    const primaryImage = restaurant.images?.find((i) => i.is_primary) || restaurant.images?.[0];
    const image =
      primaryImage?.flickr_url || restaurant.menu_item_images?.[0]?.flickr_url || null;
    return {
      id: restaurant.id,
      name: restaurant.name,
      city: mainLoc?.city || null,
      state: mainLoc?.state || null,
      image,
    };
  };

  const checkFavoriteStatus = async (restaurantId) => {
    // Guests: reflect the localStorage-backed save state instead of calling the API.
    if (!token) {
      setIsFavorited(isGuestSaved('favorites', restaurantId));
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/users/favorites/check/${restaurantId}`);
      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.is_favorited);
      }
    } catch (error) {
      console.error("Error checking favorite status:", error);
    }
  };

  const checkWishlistStatus = async (restaurantId) => {
    if (!token) {
      setIsInWishlist(isGuestSaved('wishlist', restaurantId));
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiBaseUrl}/users/wishlist/check/${restaurantId}`);
      if (response.ok) {
        const data = await response.json();
        setIsInWishlist(data.is_in_wishlist);
      }
    } catch (error) {
      console.error("Error checking wishlist status:", error);
    }
  };

  const toggleFavorite = async () => {
    // Guests: save to localStorage (merged into the account on login). See #13.
    if (!token) {
      setIsFavorited(toggleGuestSave('favorites', buildSaveSnapshot()));
      return;
    }

    setFavoritesLoading(true);
    try {
      if (isFavorited) {
        // Remove from favorites
        const response = await authenticatedFetch(`${apiBaseUrl}/users/favorites/${restaurant.id}`, {
          method: 'DELETE'
        });
        if (response.ok || response.status === 204) {
          setIsFavorited(false);
        }
      } else {
        // Add to favorites
        const response = await authenticatedFetch(`${apiBaseUrl}/users/favorites/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            restaurant_id: restaurant.id
          })
        });
        if (response.ok) {
          setIsFavorited(true);
        }
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      alert("Failed to update favorites. Please try again.");
    } finally {
      setFavoritesLoading(false);
    }
  };

  const toggleWishlist = async () => {
    if (!token) {
      setIsInWishlist(toggleGuestSave('wishlist', buildSaveSnapshot()));
      return;
    }

    setWishlistLoading(true);
    try {
      if (isInWishlist) {
        // Remove from wishlist
        const response = await authenticatedFetch(`${apiBaseUrl}/users/wishlist/${restaurant.id}`, {
          method: 'DELETE'
        });
        if (response.ok || response.status === 204) {
          setIsInWishlist(false);
        }
      } else {
        // Add to wishlist
        const response = await authenticatedFetch(`${apiBaseUrl}/users/wishlist/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            restaurant_id: restaurant.id
          })
        });
        if (response.ok) {
          setIsInWishlist(true);
        }
      }
    } catch (error) {
      console.error("Error toggling wishlist:", error);
      alert("Failed to update wishlist. Please try again.");
    } finally {
      setWishlistLoading(false);
    }
  };

  // Detect whether the logged-in user has already reviewed this restaurant (#38),
  // so we suppress the new-review form and offer Edit instead. Re-runs after a
  // review/visit submit (refreshReviews) so the UI updates immediately.
  useEffect(() => {
    if (!user || !id) {
      setMyReview(null);
      return;
    }
    let cancelled = false;
    fetch(`${apiBaseUrl}/reviews/?restaurant_id=${id}&limit=200`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled) return;
        const reviews = Array.isArray(data) ? data : data.reviews || data.items || [];
        setMyReview(reviews.find((rv) => rv.user_id === user.id) || null);
      })
      .catch(() => {
        if (!cancelled) setMyReview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, user, token, refreshReviews, apiBaseUrl]);

  // Overall restaurant rating (#130) — mirrors the Food page's #125 headline.
  // Refetched when a review/visit is added (refreshReviews).
  useEffect(() => {
    if (!id) return;
    let active = true;
    fetch(`${apiBaseUrl}/reviews/stats/aggregated?restaurant_id=${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active) setAggregation(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [id, apiBaseUrl, refreshReviews]);

  useEffect(() => {
    // Fetch restaurant details
    authenticatedFetch(`${apiBaseUrl}/restaurants/${id}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("Received restaurant details:", data);
        setRestaurant(data);
        setPageTitle(data.name); // Set page title for navbar
        setLoading(false);

        // Fetch menu items for this restaurant
        fetchMenuItems(data.id);

        // Fetch restaurant images from database
        fetchRestaurantImages(data.id);

        // If restaurant has a Flickr album URL, also extract images (as fallback)
        if (data.flickr_album_url) {
          extractFlickrImages(data.flickr_album_url, data.name);
        }

        // Check if restaurant is favorited or in wishlist
        checkFavoriteStatus(data.id);
        checkWishlistStatus(data.id);
      })
      .catch((error) => {
        console.error("Error fetching restaurant details:", error);
        setError("Failed to load restaurant details");
        setLoading(false);
      });
  }, [id, apiBaseUrl, token, setPageTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Container fluid style={{ padding: "20px" }}>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (error || !restaurant) {
    return (
      <Container fluid style={{ padding: "20px" }}>
        <Alert variant="danger">
          {error || "Restaurant not found"}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          ← Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="restaurant-detail" style={{ padding: "20px" }}>
      {/* Compact Header with inline categories/cuisines */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {/* Show creator if added by a user (not admin) */}
            {restaurant.created_by_user_id && restaurant.created_by_display_name && (
              <div className="mb-2">
                <small className="text-muted">
                  {user && restaurant.created_by_user_id === user.id ? (
                    <>You added this restaurant</>
                  ) : (
                    <>
                      Added by:{" "}
                      <span
                        style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}
                        onClick={() => navigate(`/profile/${restaurant.created_by_user_id}`)}
                      >
                        {restaurant.created_by_display_name}
                      </span>
                    </>
                  )}
                  {restaurant.created_by_is_verified && (
                    <Badge bg="success" className="ms-1" style={{ fontSize: '0.65rem' }}>✓ Verified</Badge>
                  )}
                </small>
              </div>
            )}

            {/* Inline Categories and Cuisines — single row, scroll for overflow */}
            <div className="dz-badge-scroll">
              {restaurant.categories && restaurant.categories.length > 0 && restaurant.categories.map((category) => (
                <Badge
                  key={category.id}
                  bg="primary"
                  pill
                  style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={() => navigate(`/restaurant-category/${category.id}/${generateSlug(category.name)}`)}
                >
                  {category.name}
                </Badge>
              ))}
              {restaurant.cuisines && restaurant.cuisines.length > 0 && restaurant.cuisines.map((cuisine) => (
                <Badge
                  key={cuisine.id}
                  bg="success"
                  pill
                  style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={() => navigate(`/cuisine/${cuisine.id}/${generateSlug(cuisine.name)}`)}
                  title={`View ${cuisine.name} cuisine details`}
                >
                  {cuisine.name}
                </Badge>
              ))}
            </div>
          </div>

          {/* Favorite and Wishlist Buttons — available to guests too (saved
              locally and merged into the account on login; see #13) */}
          {(
            <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
              <Button
                variant={isFavorited ? "danger" : "outline-danger"}
                size="sm"
                onClick={toggleFavorite}
                disabled={favoritesLoading}
                title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                style={{
                  fontSize: '1.5rem',
                  padding: '0.25rem 0.5rem',
                  lineHeight: '1',
                  border: 'none',
                  background: 'transparent'
                }}
                className={isFavorited ? 'text-danger' : 'text-muted'}
              >
                {favoritesLoading ? (
                  <Spinner animation="border" size="sm" style={{ fontSize: '0.75rem' }} />
                ) : (
                  isFavorited ? "❤️" : "🤍"
                )}
              </Button>

              <Button
                variant={isInWishlist ? "primary" : "outline-primary"}
                size="sm"
                onClick={toggleWishlist}
                disabled={wishlistLoading}
                title={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                style={{
                  fontSize: '1.5rem',
                  padding: '0.25rem 0.5rem',
                  lineHeight: '1',
                  border: 'none',
                  background: 'transparent'
                }}
                className={isInWishlist ? 'text-primary' : 'text-muted'}
              >
                {wishlistLoading ? (
                  <Spinner animation="border" size="sm" style={{ fontSize: '0.75rem' }} />
                ) : (
                  isInWishlist ? "📌" : "📍"
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Status banner (#137/#149/#158): the three private states, from the
            shared helper so the banner, the card badge and the profile list can't
            drift apart again. Null for public → no banner. */}
        {ownershipStatus(restaurant) && (() => {
          const status = ownershipStatus(restaurant);
          return (
            <Alert variant={status.variant} className="d-flex align-items-center gap-2 mt-3" style={{ width: 'fit-content' }}>
              <span>{status.icon} <strong>{status.label}</strong></span>
              <span className="ms-2">{status.hint}</span>
            </Alert>
          );
        })()}

        {/* Page-level actions — Edit (owner/admin, #71) and Add a photo (any
            signed-in user, #94). Kept side by side so "Add a photo" reads as a
            restaurant-or-dish contribution, not a restaurant-gallery action (#106). */}
        {user && (
          <div className="d-flex flex-wrap gap-2 mt-3">
            {(restaurant.created_by_user_id === user.id || user.is_superuser) && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(`/restaurant/${id}/edit`)}
              >
                ✏️ Edit restaurant
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => setShowContribute(true)}>
              📷 Add a photo
            </Button>
          </div>
        )}
      </div>

      {/* Overall restaurant rating headline (#130) — parity with the Food page's
          #125 block so a place's rating is visible at a glance, not buried in the
          reviews. Same endpoint, restaurant_id-scoped. */}
      <div className="mb-4">
        {aggregation && aggregation.total_reviews > 0 ? (
          <div className="d-flex align-items-center gap-2">
            <span style={{ color: '#f0a500', fontSize: '1.35rem', lineHeight: 1 }}>★</span>
            <span className="h5 mb-0">{aggregation.average_rating.toFixed(1)}</span>
            <span className="text-muted">
              · {aggregation.total_reviews} review{aggregation.total_reviews !== 1 ? 's' : ''}
            </span>
          </div>
        ) : (
          <div className="text-muted small">No ratings yet</div>
        )}
      </div>

      <Row>
        <Col md={8}>
          {/* Restaurant Description — above photos (parity with iOS) */}
          {restaurant.description && (
            <Card className="mb-4">
              <Card.Body>
                <Card.Title>About</Card.Title>
                <Card.Text style={{ whiteSpace: 'pre-line' }}>{renderRichText(restaurant.description)}</Card.Text>
              </Card.Body>
            </Card>
          )}

          <ContributePhotoModal
            show={showContribute}
            onHide={() => setShowContribute(false)}
            restaurant={restaurant}
            menuItems={menuItems}
          />

          {/* Photo Gallery - Hero Section */}
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">📸 Photos</h4>
                <div className="d-flex gap-2">
                  {/* "Add a photo" now lives in the page-level action row above (#106) */}
                  {restaurant.flickr_album_url && (
                    <a href={restaurant.flickr_album_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                      View Full Album →
                    </a>
                  )}
                </div>
              </div>

              {imagesLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading photos...</div>
                </div>
              ) : restaurantImages.length > 0 ? (
                  <Row className="g-2">
                    {restaurantImages.map((image) => {
                      const imageLocation = image.location_id && restaurant.locations
                        ? restaurant.locations.find(loc => loc.id === image.location_id)
                        : null;
                      return (
                        <Col key={image.id} xs={6} sm={6} md={4} lg={3} className="p-1">
                          <div className="position-relative" style={{ width: '100%', paddingBottom: '100%', overflow: 'hidden', borderRadius: '8px' }}>
                            <Image
                              src={image.flickr_url}
                              alt={image.caption || `${restaurant.name} photo`}
                              className="shadow-sm"
                              style={{
                                cursor: 'pointer',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px'
                              }}
                              onClick={() => window.open(image.flickr_url, '_blank')}
                              onError={(e) => {
                                const container = e.target.closest('.col-6, .col-sm-6, .col-md-4, .col-lg-3');
                                if (container) container.style.display = 'none';
                              }}
                            />
                            {image.caption && (
                              <div className="image-caption-overlay small" style={{ fontSize: '0.65rem', position: 'absolute' }}>
                                {image.caption}
                              </div>
                            )}
                            {image.is_primary === 1 && (
                              <Badge bg="primary" className="position-absolute top-0 start-0 m-1" style={{ fontSize: '0.6rem', padding: '0.15rem 0.3rem' }}>
                                Featured
                              </Badge>
                            )}
                            {imageLocation && (
                              <Badge bg="info" className="position-absolute top-0 end-0 m-1" style={{ fontSize: '0.6rem', padding: '0.15rem 0.3rem' }}>
                                {imageLocation.city}
                              </Badge>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                ) : flickrImages.length > 0 ? (
                  <Row className="g-2">
                    {flickrImages.map((imageUrl, index) => (
                      <Col key={index} xs={6} sm={6} md={4} lg={3} className="p-1">
                        <div style={{ width: '100%', paddingBottom: '100%', position: 'relative', overflow: 'hidden', borderRadius: '8px' }}>
                          <Image
                            src={imageUrl}
                            alt={`${restaurant.name} photo ${index + 1}`}
                            className="shadow-sm"
                            style={{
                              cursor: 'pointer',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '8px'
                            }}
                            onClick={() => window.open(imageUrl, '_blank')}
                            onError={(e) => {
                              const container = e.target.closest('.col-6, .col-sm-6, .col-md-4, .col-lg-3');
                              if (container) container.style.display = 'none';
                            }}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <div style={{ fontSize: '2.5rem' }}>📷</div>
                    <div className="mt-2">No photos yet</div>
                    {isInWishlist && (
                      <div className="small mt-1">Add some after your visit!</div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>

          {/* Menu Items */}
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title className="mb-0">Menu</Card.Title>
                {restaurant.menu_url && (
                  <a
                    href={restaurant.menu_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    📄 View Full Menu →
                  </a>
                )}
              </div>
              {menuLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <div className="mt-2 small text-muted">Loading menu...</div>
                </div>
              ) : menuItems.length > 0 ? (
                <Row>
                  {menuItems
                    .filter(item => item.is_available)
                    .map((item) => {
                      // Get primary image or first image
                      const primaryImage = item.images?.find(img => img.is_primary);
                      const displayImage = primaryImage || item.images?.[0];

                      return (
                        <Col key={item.id} sm={6} md={4} lg={6} xl={4} className="mb-3">
                          <Card className="h-100 menu-item-card">
                            {/* Menu Item Image */}
                            {displayImage && (
                              <div className="menu-item-image-container">
                                {/* #234: honours the per-dish crop the #117 bridge
                                    carries across from the diner's visit photo, so
                                    a wide table shot shows THIS dish rather than
                                    standing in whole. Uncropped rows render exactly
                                    as before. */}
                                <CroppedImage
                                  src={displayImage.flickr_url}
                                  crop={displayImage}
                                  alt={displayImage.caption || item.custom_name || item.food.name}
                                  className="card-img-top menu-item-image"
                                  onError={(e) => {
                                    // Hide entire image container on error to prevent blank spots
                                    const container = e.target.closest('.menu-item-image-container');
                                    if (container) {
                                      container.style.display = 'none';
                                    }
                                  }}
                                />
                                {item.images?.length > 1 && (
                                  <div className="image-count-badge">
                                    📷 {item.images.length}
                                  </div>
                                )}
                                {displayImage.caption && (
                                  <div className="image-caption-overlay">
                                    {displayImage.caption}
                                  </div>
                                )}
                              </div>
                            )}

                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <Card.Title className="h6 mb-0">
                                  <span
                                    style={{ cursor: 'pointer', color: '#0d6efd' }}
                                    onClick={() => navigate(`/food/${item.food.id}/${generateSlug(item.food.name)}`)}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                  >
                                    {item.custom_name || item.food.name}
                                  </span>
                                  {item.secondary_foods && item.secondary_foods.length > 0 && (
                                    <span className="text-muted small">
                                      {' + '}
                                      {item.secondary_foods.map(sf => sf.name).join(', ')}
                                    </span>
                                  )}
                                </Card.Title>
                                {/* #211: the scalar price is the CHEAPEST variant when
                                    this item has several, so it needs "from"; and it
                                    may be priced per pound, so it needs a unit. */}
                                <Badge bg="success" className="ms-2">
                                  {menuItemPrice(item) ?? 'Price varies'}
                                </Badge>
                              </div>

                              {/* #211: "from $X" above raises the question this answers.
                                  Only shown when there is a genuine choice — a single
                                  variant is already fully described by the badge. */}
                              {hasPriceVariants(item) && (
                                <div className="mb-2 small">
                                  {orderedPrices(item).map((variant) => (
                                    <div key={variant.id} className="d-flex justify-content-between">
                                      <span className="text-muted">{variantName(variant)}</span>
                                      <span className="fw-semibold text-success">
                                        {amount(variant.price)}{unitSuffix(item.price_unit)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* This dish's rating at this restaurant (#125),
                                  aggregated over its (restaurant, food) reviews. */}
                              {item.total_reviews > 0 && (
                                <div className="mb-2 small">
                                  <span style={{ color: '#ffc107' }}>★</span>{' '}
                                  <span className="fw-semibold">{item.average_rating.toFixed(1)}</span>{' '}
                                  <span className="text-muted">
                                    ({item.total_reviews} review{item.total_reviews !== 1 ? 's' : ''})
                                  </span>
                                </div>
                              )}

                              {item.custom_description && (
                                <Card.Text className="small text-muted mb-2" style={{ whiteSpace: 'pre-line' }}>
                                  {renderRichText(item.custom_description)}
                                </Card.Text>
                              )}

                              {(() => {
                                // Combine categories from primary food and all secondary foods
                                const allCategories = [...(item.food.categories || [])];
                                if (item.secondary_foods && item.secondary_foods.length > 0) {
                                  item.secondary_foods.forEach(sf => {
                                    if (sf.categories) {
                                      sf.categories.forEach(cat => {
                                        // Add category if not already in the list
                                        if (!allCategories.find(c => c.id === cat.id)) {
                                          allCategories.push(cat);
                                        }
                                      });
                                    }
                                  });
                                }

                                return allCategories.length > 0 && (
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    {allCategories.map((category) => (
                                      <Badge
                                        key={category.id}
                                        bg="light"
                                        text="dark"
                                        className="small"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/food-category/${category.id}/${generateSlug(category.name)}`)}
                                      >
                                        {category.name}
                                      </Badge>
                                    ))}
                                  </div>
                                );
                              })()}

                              {/* Additional food info */}
                              <div className="d-flex flex-wrap gap-1">
                                {item.food.is_vegetarian && (
                                  <Badge bg="success" className="small">V</Badge>
                                )}
                                {item.food.is_vegan && (
                                  <Badge bg="success" className="small">VG</Badge>
                                )}
                                {item.food.is_gluten_free && (
                                  <Badge bg="warning" className="small">GF</Badge>
                                )}
                                {item.food.calories && (
                                  <Badge bg="info" className="small">{item.food.calories} cal</Badge>
                                )}
                              </div>

                              {/* Multiple Images Gallery */}
                              {item.images?.length > 1 && (
                                <div className="mt-2">
                                  <div className="additional-images">
                                    {item.images.slice(0, 3).map((image, index) => (
                                      <CroppedImage
                                        key={image.id}
                                        src={image.flickr_url}
                                        crop={image}
                                        alt={image.caption || `${item.food.name} ${index + 1}`}
                                        className="additional-image-thumb"
                                        title={image.caption}
                                        // Clicking always opens the ORIGINAL, whole
                                        // photo. The crop says which dish the thumb
                                        // is about; it does not claim the rest of
                                        // the shot is not there.
                                        onClick={() => window.open(image.flickr_url, '_blank')}
                                        onError={(e) => {
                                          // When cropped, the class sits on the frame
                                          // and e.target is the image inside it —
                                          // hiding only the image would leave an empty
                                          // bordered box. closest() resolves to the
                                          // image itself in the uncropped case.
                                          const el = e.target.closest('.additional-image-thumb') || e.target;
                                          el.style.display = 'none';
                                        }}
                                      />
                                    ))}
                                    {item.images.length > 3 && (
                                      <div className="more-images-indicator">
                                        +{item.images.length - 3} more
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                </Row>
              ) : (
                <div className="text-center py-4 text-muted">
                  <div className="mb-2">🍽️</div>
                  <div>No menu items available at this time</div>
                </div>
              )}
            </Card.Body>
          </Card>

        </Col>

        <Col md={4}>
          {/* Locations - Sidebar */}
          {restaurant.locations && restaurant.locations.length > 0 && (
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">📍 Location{restaurant.locations.length > 1 ? 's' : ''}</h5>

                <Row className="g-2 mb-3">
                  {restaurant.locations.map((location) => {
                    const statusBadge = getLocationStatusBadge(location);
                    const cardStyle = getLocationCardStyle(location);
                    const hasCoordinates = location.latitude && location.longitude;
                    const mapsUrl = hasCoordinates
                      ? `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`
                      : null;

                    return (
                      <Col key={location.id} xs={restaurant.locations.length > 1 ? 6 : 12}>
                        <div
                          className="p-3 border rounded text-center position-relative"
                          style={{
                            ...cardStyle,
                            minHeight: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                        >
                          {location.is_main_location && (
                            <Badge bg="primary" className="position-absolute top-0 start-0 m-1" style={{ fontSize: '0.6rem' }}>
                              Main
                            </Badge>
                          )}
                          {statusBadge && (
                            <Badge
                              className="position-absolute top-0 end-0 m-1"
                              style={{
                                fontSize: '0.6rem',
                                backgroundColor: statusBadge.bgColor,
                                color: statusBadge.textColor || '#fff'
                              }}
                            >
                              {statusBadge.text}
                            </Badge>
                          )}
                          <div className="fw-semibold mb-1" style={{ fontSize: '0.9rem' }}>
                            {location.name || `${location.city}${location.state ? `, ${location.state}` : ''}`}
                          </div>
                          {location.name && (
                            <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
                              {location.city}{location.state ? `, ${location.state}` : ''}
                            </div>
                          )}
                          {hasCoordinates ? (
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="small"
                              style={{
                                fontSize: '0.75rem',
                                lineHeight: '1.3',
                                textDecoration: 'none',
                                color: '#0d6efd'
                              }}
                            >
                              {location.address}{location.unit ? ` ${location.unit}` : ''}
                            </a>
                          ) : (
                            <div className="small text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.3' }}>
                              {location.address}{location.unit ? ` ${location.unit}` : ''}
                            </div>
                          )}
                          {location.phone && (
                            <div className="small text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                              📞 {location.phone}
                            </div>
                          )}

                          {/* Operating Hours Section */}
                          {(() => {
                            const hoursGroups = groupHours(location);
                            if (hoursGroups.length === 0) return null;

                            // Server-computed, timezone-aware (#39) and tolerant of hours
                            // that only state the meridiem on the end (#185). Parsing this
                            // here again is what made the detail page disagree with the
                            // list card (#186). null = unknown, and the badge stays hidden.
                            const isOpen = location.currently_open ?? null;

                            // Determine current day index (Monday = 0)
                            let currentDayIndex;
                            try {
                              if (location.timezone) {
                                const restaurantTime = DateTime.now().setZone(location.timezone);
                                currentDayIndex = (restaurantTime.weekday - 1 + 7) % 7; // luxon: 1=Mon, convert to 0=Mon
                              } else {
                                const now = new Date();
                                currentDayIndex = (now.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
                              }
                            } catch (error) {
                              const now = new Date();
                              currentDayIndex = (now.getDay() + 6) % 7;
                            }

                            return (
                              <div className="hours-section mt-2 pt-2" style={{ borderTop: '1px solid #e9ecef' }}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <span className="small fw-semibold" style={{ fontSize: '0.75rem', color: '#495057' }}>
                                    🕐 Hours
                                  </span>
                                  {/* #284 — say how much longer, and carry the split
                                      lunch/dinner tail ("Closes in 15m · reopens 5 PM")
                                      that only a LOCATION can answer. Falls back to the
                                      plain Open/Closed verdict when the closing time is
                                      unknown, so this never shows less than before. */}
                                  {(() => {
                                    const status = openStatus(location);
                                    if (status) {
                                      return (
                                        <Badge
                                          bg={status.variant}
                                          style={{ fontSize: '0.65rem', fontWeight: '600' }}
                                        >
                                          {status.detail}
                                        </Badge>
                                      );
                                    }
                                    if (isOpen === null) return null;
                                    return (
                                      <Badge
                                        bg={isOpen ? 'success' : 'danger'}
                                        style={{ fontSize: '0.65rem', fontWeight: '600' }}
                                      >
                                        {isOpen ? 'Open Now' : 'Closed'}
                                      </Badge>
                                    );
                                  })()}
                                </div>
                                <div style={{ fontSize: '0.7rem' }}>
                                  {hoursGroups.map((group, groupIdx) => {
                                    const includesCurrentDay = currentDayIndex >= group.startIndex && currentDayIndex <= group.endIndex;
                                    const isClosed = group.hours.toLowerCase() === 'closed';
                                    const is24Hours = group.hours.toLowerCase() === '24 hours' || group.hours.toLowerCase() === '24/7';

                                    return (
                                      <div
                                        key={groupIdx}
                                        className="d-flex justify-content-between align-items-center"
                                        style={{
                                          padding: '0.25rem 0',
                                          backgroundColor: includesCurrentDay ? '#f8f9fa' : 'transparent',
                                          marginLeft: includesCurrentDay ? '-0.5rem' : '0',
                                          marginRight: includesCurrentDay ? '-0.5rem' : '0',
                                          paddingLeft: includesCurrentDay ? '0.5rem' : '0',
                                          paddingRight: includesCurrentDay ? '0.5rem' : '0',
                                          borderRadius: '3px'
                                        }}
                                      >
                                        <span
                                          className="fw-semibold"
                                          style={{
                                            minWidth: '65px',
                                            color: includesCurrentDay ? '#212529' : '#6c757d'
                                          }}
                                        >
                                          {formatDayRange(group)}
                                          {includesCurrentDay && <span style={{ marginLeft: '4px', fontSize: '0.6rem' }}>•</span>}
                                        </span>
                                        <span
                                          className="text-end flex-grow-1"
                                          style={{
                                            paddingLeft: '1rem',
                                            color: isClosed ? '#dc3545' : (is24Hours ? '#28a745' : '#6c757d'),
                                            fontStyle: isClosed ? 'italic' : 'normal',
                                            fontWeight: is24Hours ? '500' : 'normal'
                                          }}
                                        >
                                          {group.hours}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}

                          {location.is_closed && location.closed_date && (
                            <div className="small mt-1" style={{
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              color: statusBadge?.variant === 'danger' ? '#dc3545' : '#6c757d'
                            }}>
                              Closed: {formatClosedDate(location.closed_date)}
                            </div>
                          )}
                          {statusBadge && statusBadge.showDate && location.expected_opening_date && (
                            <div className="small mt-1" style={{
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              color: statusBadge.bgColor
                            }}>
                              {statusBadge.dateLabel}: {formatExpectedDate(location.expected_opening_date)}
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>

                {/* Visit Website Button */}
                {restaurant.website_url && (
                  <div className="d-grid">
                    <a
                      href={restaurant.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Visit Website →
                    </a>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Reviews / Visits - Sidebar */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">⭐ Reviews / Visits</h5>
              {myReview && (
                <Alert variant="light" className="border mb-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold mb-1">Your review</div>
                      <div style={{ color: '#ffc107', fontSize: '1.1rem' }}>
                        {'★'.repeat(myReview.rating)}{'☆'.repeat(5 - myReview.rating)}
                      </div>
                      {myReview.title && <div className="fw-semibold small mt-1">{myReview.title}</div>}
                      {myReview.review_text && (
                        <div className="small text-muted mt-1">{myReview.review_text}</div>
                      )}
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={() => setShowEditReview(true)}>
                      Edit
                    </Button>
                  </div>
                  <div className="small text-muted mt-2">
                    You've already reviewed this restaurant. You can still log a visit below.
                  </div>
                </Alert>
              )}
              <ReviewForm
                restaurantId={parseInt(id)}
                targetName={restaurant?.name}
                visitOnly={!!myReview}
                menuItems={menuItems}
                /* #171: a "just for me" restaurant has no public page for a
                   review to land on, so the form must not offer to share one. */
                restaurantVisibility={restaurant?.visibility}
                restaurantReviewState={restaurant?.review_state}
                onReviewSubmitted={() => setRefreshReviews(prev => prev + 1)}
              />
            </Card.Body>
          </Card>

          <EditReviewModal
            show={showEditReview}
            onHide={() => setShowEditReview(false)}
            review={myReview}
            onReviewUpdated={() => {
              setShowEditReview(false);
              setRefreshReviews(prev => prev + 1);
            }}
          />

          <Card className="mb-4">
            <Card.Body>
              <ReviewList
                restaurantId={parseInt(id)}
                refresh={refreshReviews}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Related Restaurants Section */}
      <Card className="mb-4">
        <Card.Body>
          <Tabs defaultActiveKey="nearby" id="related-restaurants-tabs" className="mb-3">
            <Tab eventKey="nearby" title="📍 Nearby Restaurants">
              <NearbyRestaurants restaurantId={parseInt(id)} />
            </Tab>
            <Tab eventKey="similar" title="🔍 Similar Restaurants">
              <SimilarRestaurants restaurantId={parseInt(id)} />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default RestaurantDetail;